import React from 'react';
import '../styles/terms.css';

const TermsConditions = ({ onClose }) => {
    return (
        <div className="modal">
            <div className="modal-content terms-section">
                <span className="close" onClick={onClose}>&times;</span>
                <h1>Terms and Conditions</h1>
                <p>Last Updated: Fri Aug 22, 2025</p>
                <p>Welcome to Intellommerce ("Company", "we", "us", or "our"). These Terms and Conditions ("Terms")
                    constitute a legally binding agreement between you and Intellommerce regarding your use of our
                    automated digital transformation services, platforms, APIs, software, and website (collectively, the
                    "Services").</p>

                <h2>1. Definitions and Interpretation</h2>
                <p>In these Terms:</p>
                <ul>
                    <li>"Automated Services" means our AI-powered, machine learning, and automation solutions</li>
                    <li>"Client Data" means any data you submit, upload, or process through our Services</li>
                    <li>"Documentation" means user manuals, technical specifications, and API documentation</li>
                    <li>"Intellectual Property Rights" means all patents, copyrights, trademarks, trade secrets, and
                        other proprietary rights
                    </li>
                    <li>"Professional Services" means consulting, implementation, training, and support services</li>
                    <li>"User" means any individual or entity accessing or using our Services</li>
                </ul>

                <h2>2. Acceptance of Terms</h2>
                <p>By accessing, using, or subscribing to our Services, you acknowledge that you have read, understood,
                    and agree to be bound by these Terms and our Privacy Policy. If you are using our Services on behalf
                    of an organization, you represent and warrant that you have the authority to bind that organization
                    to these Terms.</p>
                <p>If you do not agree to these Terms, you must immediately discontinue use of our Services.</p>

                <h2>3. Eligibility and Registration</h2>
                <ul>
                    <li>You must be at least 18 years old or the age of majority in your jurisdiction</li>
                    <li>You must provide accurate, current, and complete registration information</li>
                    <li>Corporate users must be authorized representatives of their organizations</li>
                    <li>You must not create accounts using automated methods or false pretenses</li>
                    <li>You are responsible for maintaining the confidentiality of your credentials</li>
                </ul>

                <h2>4. Service Description and Scope</h2>
                <p>Intellommerce provides:</p>
                <ul>
                    <li>Automated business process optimization solutions</li>
                    <li>AI-powered data analytics and insights</li>
                    <li>Digital transformation consulting services</li>
                    <li>Integration platforms and APIs</li>
                    <li>Machine learning model deployment and management</li>
                    <li>Workflow automation tools</li>
                    <li>Cloud-based infrastructure and computing resources</li>
                </ul>
                <p>Specific service features, limitations, and technical requirements are detailed in our Documentation
                    and service-specific agreements.</p>

                <h2>5. User Accounts and Security</h2>
                <ul>
                    <li>You are responsible for all activities occurring under your account</li>
                    <li>You must immediately notify us of any unauthorized access or security breaches</li>
                    <li>We reserve the right to suspend accounts that violate these Terms</li>
                    <li>Multi-factor authentication may be required for certain Services</li>
                    <li>You must not share login credentials or API keys with unauthorized parties</li>
                    <li>Password requirements: minimum 12 characters, including uppercase, lowercase, numbers, and
                        special characters
                    </li>
                </ul>

                <h2>6. Acceptable Use Policy</h2>
                <p>You agree not to:</p>
                <ul>
                    <li>Use Services for illegal, harmful, or fraudulent purposes</li>
                    <li>Violate any applicable laws, regulations, or third-party rights</li>
                    <li>Transmit malware, viruses, or harmful code</li>
                    <li>Attempt to gain unauthorized access to systems or networks</li>
                    <li>Engage in cryptocurrency mining without explicit permission</li>
                    <li>Overwhelm or disrupt our infrastructure through excessive usage</li>
                    <li>Reverse engineer, decompile, or disassemble our software</li>
                    <li>Use automated systems to scrape or extract data without permission</li>
                    <li>Resell or redistribute Services without authorization</li>
                    <li>Impersonate others or misrepresent your affiliation</li>
                </ul>

                <h2>7. Intellectual Property Rights</h2>
                <h3>7.1 Our Intellectual Property</h3>
                <p>All Services, including software, algorithms, models, interfaces, Documentation, and content, are
                    protected by intellectual property laws. We retain all rights, title, and interest in our Services
                    and any improvements or derivatives thereof.</p>

                <h3>7.2 License Grant to Users</h3>
                <p>Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to
                    access and use our Services solely for your authorized business purposes.</p>

                <h3>7.3 User Content and Data</h3>
                <ul>
                    <li>You retain ownership of your Client Data</li>
                    <li>You grant us a license to process Client Data solely to provide Services</li>
                    <li>We may use aggregated, anonymized data for service improvement and analytics</li>
                    <li>You represent that you have all necessary rights to use and share Client Data</li>
                </ul>

                <h2>8. Data Protection and Privacy</h2>
                <ul>
                    <li>We process personal data in accordance with our Privacy Policy and applicable data protection
                        laws
                    </li>
                    <li>We implement industry-standard security measures including encryption, access controls, and
                        regular audits
                    </li>
                    <li>For EU users, we comply with GDPR requirements</li>
                    <li>For California users, we comply with CCPA requirements</li>
                    <li>We will enter into Data Processing Agreements upon request for enterprise customers</li>
                    <li>You are responsible for obtaining necessary consents for data processing</li>
                </ul>

                <h2>9. AI and Automation-Specific Terms</h2>
                <ul>
                    <li>AI outputs are provided "as-is" and may contain errors or biases</li>
                    <li>You must review and validate all automated decisions before implementation</li>
                    <li>We do not guarantee specific accuracy rates or performance metrics</li>
                    <li>Machine learning models may be updated periodically, affecting outputs</li>
                    <li>You acknowledge the probabilistic nature of AI technologies</li>
                    <li>Human oversight is required for critical decision-making processes</li>
                </ul>

                <h2>10. Service Level Agreement (SLA)</h2>
                <p>For enterprise customers:</p>
                <ul>
                    <li>Target uptime: 99.9% availability excluding scheduled maintenance</li>
                    <li>Scheduled maintenance windows: Sundays 2:00-6:00 AM UTC</li>
                    <li>Support response times vary by service tier</li>
                    <li>Service credits may be available for extended downtime</li>
                    <li>Detailed SLA terms provided in enterprise agreements</li>
                </ul>

                <h2>11. Payment Terms</h2>
                <ul>
                    <li>Fees are specified in your service agreement or subscription plan</li>
                    <li>Payment is due within 30 days of invoice unless otherwise agreed</li>
                    <li>Late payments may incur interest at 1.5% per month</li>
                    <li>All fees are non-refundable unless explicitly stated</li>
                    <li>We reserve the right to suspend Services for non-payment</li>
                    <li>Prices may be adjusted with 30 days' notice</li>
                </ul>

                <h2>12. Professional Services</h2>
                <p>If you purchase Professional Services:</p>
                <ul>
                    <li>Specific deliverables will be defined in Statements of Work</li>
                    <li>You must provide necessary access and cooperation</li>
                    <li>Delays caused by you may result in additional charges</li>
                    <li>Change requests must be approved in writing</li>
                    <li>Travel expenses may be charged separately</li>
                </ul>

                <h2>13. Third-Party Services and Integrations</h2>
                <ul>
                    <li>Our Services may integrate with third-party platforms</li>
                    <li>We are not responsible for third-party service availability or performance</li>
                    <li>Your use of third-party services is subject to their terms</li>
                    <li>We do not endorse or warrant third-party services</li>
                    <li>API integrations are subject to rate limits and usage quotas</li>
                </ul>

                <h2>14. Warranties and Disclaimers</h2>
                <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                <ul>
                    <li>SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE"</li>
                    <li>WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED</li>
                    <li>WE DO NOT WARRANT UNINTERRUPTED OR ERROR-FREE OPERATION</li>
                    <li>WE DO NOT WARRANT FITNESS FOR A PARTICULAR PURPOSE</li>
                    <li>YOU ASSUME ALL RISKS ASSOCIATED WITH SERVICE USE</li>
                </ul>

                <h2>15. Limitation of Liability</h2>
                <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                <ul>
                    <li>OUR TOTAL LIABILITY SHALL NOT EXCEED FEES PAID IN THE PRECEDING 12 MONTHS</li>
                    <li>WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES</li>
                    <li>WE ARE NOT LIABLE FOR LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION</li>
                    <li>THESE LIMITATIONS APPLY REGARDLESS OF THE THEORY OF LIABILITY</li>
                    <li>SOME JURISDICTIONS DO NOT ALLOW THESE LIMITATIONS</li>
                </ul>

                <h2>16. Indemnification</h2>
                <p>You agree to defend, indemnify, and hold harmless Intellommerce, its affiliates, and their respective
                    officers, directors, employees, and agents from any claims, damages, losses, and expenses (including
                    reasonable attorney fees) arising from:</p>
                <ul>
                    <li>Your violation of these Terms</li>
                    <li>Your use or misuse of the Services</li>
                    <li>Your violation of any laws or third-party rights</li>
                    <li>Your Client Data or content</li>
                    <li>Your negligence or willful misconduct</li>
                </ul>

                <h2>17. Confidentiality</h2>
                <ul>
                    <li>Each party will protect the other's confidential information</li>
                    <li>Confidential information excludes publicly available information</li>
                    <li>Obligations survive termination for 5 years</li>
                    <li>Disclosures may be made if legally required</li>
                    <li>We may disclose your use of our Services for marketing with your consent</li>
                </ul>

                <h2>18. Export Compliance</h2>
                <ul>
                    <li>Services are subject to U.S. export control laws</li>
                    <li>You may not use Services in embargoed countries</li>
                    <li>You represent that you are not on any prohibited party lists</li>
                    <li>You will not use Services for prohibited end uses</li>
                </ul>

                <h2>19. Termination</h2>
                <ul>
                    <li>Either party may terminate for convenience with 30 days' notice</li>
                    <li>We may terminate immediately for Terms violations</li>
                    <li>Upon termination, your access will be disabled</li>
                    <li>You must pay all outstanding fees</li>
                    <li>We will assist with data export for 30 days post-termination</li>
                    <li>Certain provisions survive termination</li>
                </ul>

                <h2>20. Force Majeure</h2>
                <p>Neither party is liable for delays or failures due to causes beyond reasonable control, including but
                    not limited to acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or
                    military authorities, fire, floods, accidents, pandemic, strikes, or shortages of transportation,
                    facilities, fuel, energy, labor, or materials.</p>

                <h2>21. Dispute Resolution</h2>
                <h3>21.1 Informal Resolution</h3>
                <p>Before initiating formal proceedings, parties agree to attempt good faith resolution through direct
                    communication. Contact us at legal@intellommerce.ai within 30 days of the dispute arising.</p>

                <h3>21.2 Binding Arbitration</h3>
                <ul>
                    <li>Disputes shall be resolved through binding arbitration under JAMS rules</li>
                    <li>Arbitration location: Flanders, Belgium</li>
                    <li>Each party bears its own attorney fees unless otherwise awarded</li>
                    <li>Arbitrator decisions are final and binding</li>
                    <li>Class action waiver: No class or representative actions</li>
                </ul>

                <h3>21.3 Exceptions to Arbitration</h3>
                <ul>
                    <li>Small claims court actions within jurisdictional limits</li>
                    <li>Injunctive relief for intellectual property violations</li>
                    <li>Disputes related to data security breaches</li>
                </ul>

                <h2>22. Governing Law</h2>
                <ul>
                    <li>These Terms are governed by the laws of Flanders, Belgium, without regard to conflict of law
                        principles
                    </li>
                    <li>The United Nations Convention on Contracts for the International Sale of Goods does not apply
                    </li>
                    <li>Exclusive jurisdiction for non-arbitrable disputes: courts of Flanders, Belgium</li>
                    <li>Service-specific regulations may apply based on your location</li>
                </ul>

                <h2>23. Severability and Waiver</h2>
                <ul>
                    <li>If any provision is found unenforceable, remaining provisions continue in full effect</li>
                    <li>Unenforceable provisions will be modified to achieve intended purpose</li>
                    <li>Failure to enforce any right does not constitute a waiver</li>
                    <li>Waivers must be in writing and signed by authorized representatives</li>
                </ul>

                <h2>24. Assignment and Transfer</h2>
                <ul>
                    <li>You may not assign these Terms without our written consent</li>
                    <li>We may assign our rights to affiliates or successors</li>
                    <li>Terms remain binding on permitted successors and assigns</li>
                    <li>Notice will be provided for assignments affecting service delivery</li>
                </ul>

                <h2>25. Entire Agreement</h2>
                <p>These Terms, together with our Privacy Policy, Data Processing Agreement (if applicable), and any
                    service-specific agreements, constitute the entire agreement between you and Intellommerce regarding
                    the Services and supersede all prior agreements, understandings, and communications.</p>

                <h2>26. Service-Specific Terms</h2>
                <h3>26.1 API Services</h3>
                <ul>
                    <li>Rate limits apply as specified in Documentation</li>
                    <li>API keys must be kept confidential</li>
                    <li>Caching of API responses subject to data freshness requirements</li>
                    <li>Webhooks must acknowledge receipt within 5 seconds</li>
                    <li>Fair use policy applies to prevent system abuse</li>
                </ul>

                <h3>26.2 Data Processing Services</h3>
                <ul>
                    <li>Processing capacity subject to plan limits</li>
                    <li>Data retention periods as specified in service agreements</li>
                    <li>Batch processing may be queued during peak times</li>
                    <li>Real-time processing subject to availability</li>
                </ul>

                <h3>26.3 Machine Learning Services</h3>
                <ul>
                    <li>Model training requires minimum data quality standards</li>
                    <li>Model performance not guaranteed for all use cases</li>
                    <li>Retraining may affect model outputs</li>
                    <li>Custom models remain your intellectual property</li>
                </ul>

                <h2>27. Beta Services and Features</h2>
                <ul>
                    <li>Beta services provided "as is" without warranties</li>
                    <li>May be discontinued without notice</li>
                    <li>Feedback and usage data may be used for improvement</li>
                    <li>Not recommended for production use</li>
                    <li>Separate beta terms may apply</li>
                </ul>

                <h2>28. Open Source Components</h2>
                <ul>
                    <li>Services may include open source software</li>
                    <li>Open source licenses apply to respective components</li>
                    <li>List of open source components available upon request</li>
                    <li>No warranty provided for open source elements</li>
                </ul>

                <h2>29. Compliance and Certifications</h2>
                <p>We maintain compliance with:</p>
                <ul>
                    <li>SOC 2 Type II certification</li>
                    <li>ISO 27001 information security standards</li>
                    <li>GDPR requirements for data protection</li>
                    <li>CCPA for California consumer privacy</li>
                    <li>HIPAA compliance available for healthcare customers (BAA required)</li>
                    <li>PCI DSS for payment card processing</li>
                </ul>

                <h2>30. Accessibility</h2>
                <ul>
                    <li>We strive for WCAG 2.1 AA compliance</li>
                    <li>Accessibility feedback: accessibility@intellommerce.ai</li>
                    <li>Alternative formats available upon request</li>
                    <li>Continuous improvement of accessibility features</li>
                </ul>

                <h2>31. Notice and Communication</h2>
                <ul>
                    <li>Notices to us: legal@intellommerce.ai or registered address</li>
                    <li>Notices to you: email address on file or account dashboard</li>
                    <li>Legal notices effective upon receipt</li>
                    <li>Service notices effective upon posting</li>
                    <li>You must maintain current contact information</li>
                </ul>

                <h2>32. Anti-Corruption and Trade Compliance</h2>
                <ul>
                    <li>Compliance with Foreign Corrupt Practices Act (FCPA)</li>
                    <li>Compliance with UK Bribery Act</li>
                    <li>No use for money laundering activities</li>
                    <li>Compliance with applicable sanctions and embargoes</li>
                    <li>Know Your Customer (KYC) verification may be required</li>
                </ul>

                <h2>33. Service Credits and Refunds</h2>
                <ul>
                    <li>Service credits for downtime per SLA terms</li>
                    <li>No refunds for partial month usage</li>
                    <li>Credits applied to future invoices</li>
                    <li>Credit requests must be submitted within 30 days</li>
                    <li>Refunds at our sole discretion for exceptional circumstances</li>
                </ul>

                <h2>34. Feedback and Suggestions</h2>
                <ul>
                    <li>Feedback becomes our property without compensation</li>
                    <li>We may use suggestions without attribution</li>
                    <li>No obligation to implement feedback</li>
                    <li>No confidentiality for unsolicited suggestions</li>
                </ul>

                <h2>35. Publicity Rights</h2>
                <ul>
                    <li>We may list you as a customer with your consent</li>
                    <li>Case studies require separate written agreement</li>
                    <li>You may opt-out of customer lists anytime</li>
                    <li>Testimonials used only with permission</li>
                </ul>

                <h2>36. Insurance Requirements</h2>
                <p>We maintain:</p>
                <ul>
                    <li>General liability insurance: $5 million</li>
                    <li>Professional liability/E&O: $5 million</li>
                    <li>Cyber liability insurance: $10 million</li>
                    <li>Workers compensation as required by law</li>
                </ul>

                <h2>37. Audit Rights</h2>
                <p>Enterprise customers may:</p>
                <ul>
                    <li>Request security audit reports (SOC 2, pen test results)</li>
                    <li>Conduct audits with 30 days notice (once annually)</li>
                    <li>Audit costs borne by requesting party</li>
                    <li>Subject to confidentiality agreements</li>
                </ul>

                <h2>38. Subprocessors</h2>
                <ul>
                    <li>Current list available at: intellommerce.ai?modal=subprocessors</li>
                    <li>30 days notice for new subprocessors</li>
                    <li>Right to object to subprocessor changes</li>
                    <li>Subprocessors bound by equivalent terms</li>
                </ul>

                <h2>39. Language</h2>
                <ul>
                    <li>English version prevails in case of translations</li>
                    <li>Communications primarily in English</li>
                    <li>Support available in: English, Spanish, French, German, Dutch</li>
                </ul>

                <h2>40. Definitions and Interpretation</h2>
                <ul>
                    <li>"Days" means calendar days unless specified</li>
                    <li>"Include" means including but not limited to</li>
                    <li>Headings for convenience only</li>
                    <li>Singular includes plural and vice versa</li>
                </ul>

                <h2>41. Emergency Access</h2>
                <ul>
                    <li>We may access accounts to address emergencies</li>
                    <li>Emergency includes security threats, legal compliance</li>
                    <li>Notification provided as soon as practicable</li>
                    <li>Access limited to necessary scope</li>
                </ul>

                <h2>42. Changes to Terms</h2>
                <ul>
                    <li>30 days notice for material changes (email/dashboard)</li>
                    <li>Immediate changes for legal/security requirements</li>
                    <li>Continued use constitutes acceptance</li>
                    <li>Right to terminate if you disagree with changes</li>
                    <li>Archive of previous versions maintained</li>
                </ul>

                <h2>43. Contact Us</h2>
                <p>For questions about these Terms, please contact us:</p>
                <ul>
                    <li><strong>Email:</strong> legal@intellommerce.ai</li>
                    <li><strong>Support:</strong> support@intellommerce.ai</li>
                    <li><strong>Phone:</strong> +32 498 06 05 28</li>
                    <li><strong>Address:</strong> Intellommerce.ai Legal Department, De Schrijverstraat 26/01, Aalst, 9300, Belgium</li>
                    <li><strong>Business Hours:</strong> Monday-Friday, 9:00 AM - 5:00 PM CEST</li>
                </ul>

                <p>
                    <strong>Effective Date:</strong> Tue 19 Aug, 2025<br/>
                    <strong>Last Modified:</strong> Fri Aug 22, 2025<br/>
                    <strong>Version:</strong> 2.0
                </p>

                <p>By using our Services, you acknowledge that you have read, understood, and agree to be bound by these
                    Terms and Conditions.</p>
            </div>
        </div>
    );
};

export default TermsConditions;